import api from "./api";
import type { PaginatedResponse } from "@/types/borrower";

export type UsageKind =
  | "statement_processed"
  | "baselayer_run"
  | "baselayer_linked_pull"
  | "baselayer_web_presence";

export interface UsageKindStats {
  count: number;
  amount_cents: number;
  unit_price_cents: number;
}

export interface UsageBucket {
  key: string;
  applications_created: number;
  usage: Record<UsageKind, UsageKindStats>;
  total_cents: number;
}

export interface BillingAccount {
  has_payment_method: boolean;
  card_brand: string;
  card_last4: string;
  card_exp_month: number | null;
  card_exp_year: number | null;
  billing_email: string;
  stripe_publishable_key: string;
  billing_configured: boolean;
  accrued_unbilled_cents: number;
  threshold_cents: number;
  next_weekly_charge_at: string;
  statement_unit_price_cents: number;
  baselayer_unit_price_cents: number;
}

export interface BillingInvoice {
  id: number;
  period_start: string;
  period_end: string;
  trigger: "weekly" | "threshold" | "manual";
  subtotal_cents: number;
  kind_counts: Record<string, number>;
  status: "open" | "paid" | "payment_failed" | "void";
  stripe_invoice_id: string | null;
  hosted_invoice_url: string;
  invoice_pdf_url: string;
  last_error: string;
  paid_at: string | null;
  created_at: string;
}

export interface UsageEvent {
  id: number;
  kind: UsageKind;
  quantity: number;
  unit_price_cents: number;
  amount_cents: number;
  occurred_at: string;
  billable: boolean;
  borrower_id: number | null;
  borrower_name: string;
  source_type: string;
  source_id: string;
  invoice: number | null;
}

export const billingService = {
  getAccount: async (): Promise<BillingAccount> => {
    const response = await api.get<BillingAccount>("/billing/account/");
    return response.data;
  },

  createSetupIntent: async (): Promise<{ client_secret: string; setup_intent_id: string }> => {
    const response = await api.post<{ client_secret: string; setup_intent_id: string }>(
      "/billing/account/setup-intent/"
    );
    return response.data;
  },

  attachPaymentMethod: async (
    paymentMethodId: string
  ): Promise<{
    has_payment_method: boolean;
    card_brand: string;
    card_last4: string;
    card_exp_month: number | null;
    card_exp_year: number | null;
  }> => {
    const response = await api.post("/billing/account/payment-method/", {
      payment_method_id: paymentMethodId,
    });
    return response.data;
  },

  removePaymentMethod: async (): Promise<void> => {
    await api.delete("/billing/account/payment-method/");
  },

  listUsageMonths: async (
    months = 12
  ): Promise<{ months: UsageBucket[]; accrued_unbilled_cents: number }> => {
    const response = await api.get("/billing/usage/months/", { params: { months } });
    return response.data;
  },

  listUsageDays: async (yearMonth: string): Promise<{ days: UsageBucket[] }> => {
    const response = await api.get(`/billing/usage/months/${yearMonth}/days/`);
    return response.data;
  },

  listUsageEvents: async (
    date: string,
    params: { page?: number; page_size?: number } = {}
  ): Promise<PaginatedResponse<UsageEvent>> => {
    const response = await api.get<PaginatedResponse<UsageEvent>>("/billing/usage/events/", {
      params: { date, ...params },
    });
    return response.data;
  },

  listInvoices: async (
    params: { page?: number; page_size?: number } = {}
  ): Promise<PaginatedResponse<BillingInvoice>> => {
    const response = await api.get<PaginatedResponse<BillingInvoice>>("/billing/invoices/", {
      params,
    });
    return response.data;
  },

  retryInvoice: async (invoiceId: number): Promise<BillingInvoice> => {
    const response = await api.post<BillingInvoice>(`/billing/invoices/${invoiceId}/retry/`);
    return response.data;
  },
};
